import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "../api/axiosInstance";
import WorkspaceSelector from "../Components/WorkspaceSelector";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { canAccessCorporatePurchasing } from "../corporate-purchasing/corporatePurchasingAccess";
import { resolveModuleTargetAfterLogin } from "../utils/moduleEntryResolver";
import { readStoredAuthToken, readStoredUser, setActiveBusinessUnitSession } from "../utils/sessionUser";
import { filterWorkspaceBusinessUnits, hasBusinessUnitWorkspaceAccess, workspaceAccessKey } from "../utils/workspaceAccess";
import { isDemoMode } from "../demo/demoMode";
import "../LoginScreen.css";

export default function WorkspaceSelectionScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { languageId, themeId } = usePmsTheme();
  const spanish = /^es(?:-|$)/i.test(languageId || "");
  const [user, setUser] = useState(() => readStoredUser() || {});
  const accessKey = workspaceAccessKey(user);
  const [bootstrap] = useState(() => {
    const prepared = location.state?.workspaceBootstrap;
    return prepared?.accessKey === accessKey && Array.isArray(prepared.businessUnits) ? prepared : null;
  });
  const [businessUnits, setBusinessUnits] = useState(() => filterWorkspaceBusinessUnits(bootstrap?.businessUnits, user));
  const [loading, setLoading] = useState(!bootstrap);
  const [loadFailed, setLoadFailed] = useState(Boolean(bootstrap?.failed));
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);
  const catalogKey = JSON.stringify([accessKey, retry]);
  const [loadedCatalogKey, setLoadedCatalogKey] = useState(bootstrap ? catalogKey : null);
  const [switchingId, setSwitchingId] = useState(null);
  const switchingRef = useRef(false);
  const automaticAttemptRef = useRef(null);
  const mountedRef = useRef(true);
  const authenticated = Boolean(readStoredAuthToken() && user?.id);
  const localAccess = hasBusinessUnitWorkspaceAccess(user);

  useEffect(() => {
    mountedRef.current = true;
    const sync = () => setUser(readStoredUser() || {});
    window.addEventListener("palantir-session-updated", sync);
    window.addEventListener("storage", sync);
    return () => {
      mountedRef.current = false;
      window.removeEventListener("palantir-session-updated", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    if (bootstrap?.accessKey === accessKey && retry === 0) return undefined;
    setBusinessUnits([]);
    setLoadedCatalogKey(null);
    setError(null);
    setLoadFailed(false);
    if (!authenticated || !localAccess) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    axios.get("/api/business-units", { signal: controller.signal, timeout: 15_000 })
      .then(({ data }) => {
        if (!Array.isArray(data)) throw new Error("Invalid business unit response");
        if (!cancelled) {
          setBusinessUnits(filterWorkspaceBusinessUnits(data, readStoredUser()));
          setLoadedCatalogKey(catalogKey);
        }
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; controller.abort(); };
  }, [accessKey, authenticated, bootstrap, catalogKey, localAccess, retry]);

  const selectBusinessUnit = useCallback(async (unit) => {
    if (switchingRef.current || loadedCatalogKey !== catalogKey
      || !businessUnits.some((entry) => String(entry.id) === String(unit?.id))) return;
    switchingRef.current = true;
    setSwitchingId(unit.id);
    setError(null);
    let navigating = false;
    try {
      // The server returns the chosen BU's fresh role, permissions and session token.
      const updatedUser = await setActiveBusinessUnitSession(user.id, Number(unit.id));
      if (!mountedRef.current) return;
      const target = resolveModuleTargetAfterLogin({
        modules: updatedUser.allowed_modules,
        permissions: updatedUser.permissions || {},
        businessUnitId: unit.id,
        isAdmin: Number(updatedUser.id) === 1,
      });
      if (target) {
        navigating = true;
        navigate(target, { replace: true });
      }
      else setError(spanish
        ? "No tienes módulos disponibles en esta unidad. Selecciona otra o consulta al administrador."
        : "No modules are available in this business unit. Choose another or contact your administrator.");
    } catch (_error) {
      if (mountedRef.current) setError(spanish
        ? "No se ha podido acceder a esta unidad de negocio. Vuelve a intentarlo."
        : "Could not open this business unit. Please try again.");
    } finally {
      // Keep the opening state until navigation commits, without flashing the card again.
      if (!navigating) {
        switchingRef.current = false;
        if (mountedRef.current) setSwitchingId(null);
      }
    }
  }, [businessUnits, catalogKey, loadedCatalogKey, navigate, spanish, user.id]);

  // Skip a choice with only one destination, but keep independent global workspaces reachable.
  const automaticUnit = authenticated && !loading && !loadFailed && loadedCatalogKey === catalogKey
    && businessUnits.length === 1 && Number(user.id) !== 1 && !canAccessCorporatePurchasing(user)
    ? businessUnits[0] : null;
  const automaticAttemptKey = JSON.stringify([user.id, retry]);
  const enteringAutomatically = Boolean(automaticUnit && !error
    && (automaticAttemptRef.current !== automaticAttemptKey || switchingId !== null));

  useEffect(() => {
    if (!automaticUnit || error || switchingRef.current || automaticAttemptRef.current === automaticAttemptKey) return;
    // Mark before switching: session events, StrictMode and failures must not trigger another attempt.
    automaticAttemptRef.current = automaticAttemptKey;
    selectBusinessUnit(automaticUnit);
  }, [automaticAttemptKey, automaticUnit, error, selectBusinessUnit]);

  const openGlobalAccess = (target, allowed) => {
    if (!switchingRef.current && allowed) navigate(target);
  };
  const signOut = () => {
    if (switchingRef.current) return;
    if (isDemoMode()) { navigate("/demo", { replace: true }); return; }
    localStorage.removeItem("palantir_token");
    localStorage.removeItem("palantir_user");
    delete axios.defaults.headers.common.Authorization;
    window.dispatchEvent(new CustomEvent("palantir-session-updated", { detail: {} }));
    navigate("/", { replace: true });
  };

  if (!authenticated) return <Navigate to="/" replace />;
  return <WorkspaceSelector
    businessUnits={enteringAutomatically ? [] : businessUnits} loading={loading || enteringAutomatically} switchingId={switchingId}
    error={loadFailed ? (spanish
      ? "No se han podido cargar las unidades de negocio. Vuelve a intentarlo."
      : "Could not load business units. Please try again.") : error}
    languageId={languageId} themeId={themeId}
    canAccessAdmin={Number(user.id) === 1}
    canAccessCorporate={canAccessCorporatePurchasing(user)}
    onSelectBusinessUnit={selectBusinessUnit}
    onAdmin={() => openGlobalAccess("/admin/business-units", Number(readStoredUser()?.id) === 1)}
    onCorporate={() => openGlobalAccess("/corporate-purchasing/tenders", canAccessCorporatePurchasing(readStoredUser()))}
    onSignOut={signOut} onRetry={() => { if (!switchingRef.current) setRetry((value) => value + 1); }}
  />;
}
